/**
 * client/src/lib/claimLink.ts — "or send it to yourself", the one way it actually works today.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **This exists because three screens had three answers to one question, and two of them were
 * wrong.** §2.4 asks for the claim link to be shown "in large type with a copy button and an 'email
 * this to yourself' field". Three files grew that field independently:
 *
 *   `studio/Studio.tsx`  POST /sessions/:id/email-link        → on failure, `mailto:`
 *   `pages/Wait.tsx`     POST /sessions/:id/claim-link/email  → on failure, a `console.error`
 *   `pages/Reveal.tsx`   POST /sessions/:id/claim-link/email  → on failure, a `console.error`
 *
 * Two route names for one route, **neither of which the server implements**, and two of the three
 * screens answering the failure by doing nothing at all: she types her address into the last screen
 * of the session, presses Send, and the button neither sends nor says anything. On `Wait.tsx` that is
 * the *ending* of every session in this build (§3.14), which makes it the worst placed dead control
 * in the product.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── Why there is no server route here, rather than a route that pretends ────────────────────────
 *
 * Sending mail needs a transport, and this product has none: `KNOWN_KEYS` in `shared/config.ts` has
 * no SMTP host, no API key and no notification URL, `package.json` has no mail dependency, and §12.4
 * refuses to add a variable nothing reads. So a `POST /claim-link/email` written today could do
 * exactly one thing honestly — fail — and a route whose only behaviour is to fail is worse than no
 * route: it is a request on the family's last screen that costs a round trip to reach the fallback
 * that was always going to run.
 *
 * `mailto:` needs none of it. Her own mail client opens with the link already in the body, addressed
 * to the address she just typed, and it is delivered by the account she already trusts rather than by
 * a sender the recipient has never heard from — which is also the one that does not land in spam.
 *
 * ⚠️ **This is not the deferred notification.** §3.14's *"Karti will send you your voice tonight"* is
 * a message the SERVER sends when provisioning finishes, to someone who has closed the laptop. That
 * genuinely needs a transport, it is P3's, and nothing here substitutes for it. This is only the
 * "send it to yourself" field, which is a thing she does with her hands while looking at the screen.
 *
 * ── The one inaccuracy, and it is closed ────────────────────────────────────────────────────────
 *
 * Callers used to show *"Sent."* after this returned, and what had actually happened was that her
 * mail client opened with the message composed. Nothing had been sent, and nothing in this product
 * can send it — so the screen was asserting something it had no way to know, on the last screen of
 * the session, about the only durable handle to a recording of somebody's mother.
 *
 * `FAMILY.keep.linkEmailSent` is now *"Your mail app has it."*, which is exactly what happened and
 * what she is about to be looking at. The gap was recorded here rather than papered over, and this
 * paragraph is what closing it looks like.
 */

import { FAMILY } from "../../../shared/voice/copy.ts";

/**
 * Compose the message and hand it to the platform.
 *
 * The subject and body are `copy.ts` strings because they are sentences somebody reads, and the
 * retention promise travels with the link for the same reason it is on the screen: the person who
 * finds this email in two years needs to know the recording is still there.
 *
 * `url` is the server-built `claimUrl` (`{publicOrigin}/c/{token}`) and never one assembled from
 * `window.location.origin` — through the tailnet this app answers on `100.92.185.76:8912`, and a
 * claim link emailed with that in it is a link that works from exactly one house.
 *
 * Returns false when there is nothing to send, so a caller does not report success for a no-op.
 */
export function emailClaimLink(email: string, url: string): boolean {
  const to = email.trim();
  if (to === "" || url === "") return false;

  const subject = encodeURIComponent(FAMILY.keep.linkTitle);
  const body = encodeURIComponent(`${url}\n\n${FAMILY.keep.retention}`);
  // `location.href` rather than `window.open`: a popup blocker silently swallows the second one, and
  // "silently swallowed" is the failure mode this whole file exists to remove. A `mailto:` navigation
  // does not unload the page — the handler takes it — so the screen behind her is still there.
  window.location.href = `mailto:${encodeURIComponent(to)}?subject=${subject}&body=${body}`;
  return true;
}
